'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useMeasure } from 'react-use'
import { UseMeasureRef } from 'react-use/lib/useMeasure'
import { IChildrenProps } from '@/typings/children-props'
import { debounce } from '@/utils/functions/debounce'

interface IRestaurantCardsContext {
    imageHeight: number
    imageWidth: number
    imageContainerWidth: number
    firstImageContainerRef?: UseMeasureRef<HTMLDivElement>
    allowedImageWidths: number[]
    aspectRatio: number
}

const ASPECT_RATIO = 3 / 2
const LP_ASPECT_RATIO = 476 / 300

const ALLOWED_IMAGE_WIDTHS = [
    // MotoG Power, Pixel 7, etc... often used. iPhone max is larger, but only 18px
    372, 470, 550, 650, 774, 870,
]

const RestaurantCardsContext = createContext<IRestaurantCardsContext>({
    imageHeight: 0,
    imageWidth: 0,
    imageContainerWidth: 0,
    allowedImageWidths: ALLOWED_IMAGE_WIDTHS,
    aspectRatio: ASPECT_RATIO,
})

interface IRestaurantCardsProviderProps extends IChildrenProps {
    styleType: 'default' | 'lp'
}

export const RestaurantCardsProvider = ({ children, styleType }: IRestaurantCardsProviderProps) => {
    const [{ imageHeight, imageWidth }, setImageSize] = useState(() => ({
        imageHeight: 0,
        imageWidth: 0,
    }))

    const [imageContainerRef, { width: imageContainerWidth }] = useMeasure<HTMLDivElement>()

    const rawOnWindowResize = useCallback(() => {
        if (!imageContainerWidth) return

        const adjustedImageContainerWidth =
            ALLOWED_IMAGE_WIDTHS.find((allowedWidth) => allowedWidth >= imageContainerWidth) ??
            ALLOWED_IMAGE_WIDTHS[ALLOWED_IMAGE_WIDTHS.length - 1]

        flushSync(() => {
            setImageSize({
                imageWidth: adjustedImageContainerWidth,
                imageHeight:
                    styleType === 'default'
                        ? Math.round(adjustedImageContainerWidth / ASPECT_RATIO)
                        : Math.round(adjustedImageContainerWidth / LP_ASPECT_RATIO),
            })
        })

        return
    }, [imageContainerWidth, styleType])

    const debouncedOnWindowResize = useMemo(
        () => debounce(rawOnWindowResize, 100),
        [rawOnWindowResize]
    )

    const hasSyncedWindowInitially = useRef(false)
    useEffect(() => {
        if (!hasSyncedWindowInitially.current) {
            rawOnWindowResize()
            hasSyncedWindowInitially.current = true

            return
        }

        debouncedOnWindowResize()
    }, [debouncedOnWindowResize, rawOnWindowResize])

    const aspectRatio = styleType === 'default' ? ASPECT_RATIO : LP_ASPECT_RATIO

    return (
        <RestaurantCardsContext.Provider
            value={useMemo(
                () => ({
                    imageHeight,
                    imageWidth,
                    imageContainerWidth,
                    firstImageContainerRef: imageContainerRef,
                    allowedImageWidths: ALLOWED_IMAGE_WIDTHS,
                    aspectRatio,
                }),
                [imageHeight, imageWidth, imageContainerWidth, imageContainerRef, aspectRatio]
            )}
        >
            {children}
        </RestaurantCardsContext.Provider>
    )
}

export const useRestaurantCardsContext = () => useContext(RestaurantCardsContext)
